import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { apiDelete, apiGet, apiPost, apiPut } from "@/lib/api";
import { useSession } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/cn";

/** Same categories and labels as the mobile SafetyPanel (mobile/src/lib/safety.ts). */
export const REPORT_CATEGORIES = [
  { id: "harassment", label: "嫌がらせ" },
  { id: "spam", label: "迷惑行為" },
  { id: "inappropriate", label: "不適切な内容" },
  { id: "other", label: "その他" },
] as const;
export type ReportCategory = (typeof REPORT_CATEGORIES)[number]["id"];
export const REPORT_DETAILS_MAX = 2000;

export interface BlockedUser { user_id: string; display_name?: string; created_at: number }

export function blocksQueryKey(tenantId?: string, userId?: string) {
  return ["safety-blocks", tenantId ?? "", userId ?? ""] as const;
}

/** Blocked users of the signed-in member (GET /v1/safety/blocks, shared with the mobile app). */
export function useBlocks() {
  const session = useSession();
  const query = useQuery({
    queryKey: blocksQueryKey(session?.tenantId, session?.user.id),
    enabled: !!session,
    queryFn: async () => (await apiGet<{ blocks: BlockedUser[] }>("/v1/safety/blocks")).blocks,
  });
  const blockedIds = new Set((query.data ?? []).map(block => block.user_id));
  return { ...query, blockedIds };
}

export async function unblockUser(userId: string, tenantId?: string, selfId?: string): Promise<void> {
  const result = await apiDelete<{ blocked: boolean }>(`/v1/safety/blocks/${encodeURIComponent(userId)}`);
  if (result?.blocked !== false) throw new Error("解除できませんでした。");
  queryClient.setQueryData<BlockedUser[]>(blocksQueryKey(tenantId, selfId), old => (old ?? []).filter(item => item.user_id !== userId));
}

export type SafetyTarget = {
  mode: "report" | "block";
  roomId: string;
  userId: string;
  displayName: string;
  message?: { id: string; body: string };
};

type Step = "report" | "confirm" | "done" | "block";

export function SafetyDialog({ target, onClose }: { target: SafetyTarget | null; onClose: () => void }): JSX.Element | null {
  const session = useSession();
  const [step, setStep] = useState<Step>(target?.mode === "block" ? "block" : "report");
  const [category, setCategory] = useState<ReportCategory | null>(null);
  const [details, setDetails] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (!target) return null;

  const isSelf = target.userId === session?.user.id;
  const close = () => { if (!busy) onClose(); };
  const trimmed = details.trim();
  const fail = (e: unknown) => setError(e instanceof Error ? e.message : "操作できませんでした。");

  const submitReport = async () => {
    if (!category || busy || isSelf) return;
    if (trimmed.length > REPORT_DETAILS_MAX) { setError(`補足は${REPORT_DETAILS_MAX}文字以内にしてください。`); return; }
    setBusy(true); setError("");
    try {
      await apiPost("/v1/safety/reports", {
        target_type: target.message ? "message" : "user",
        target_id: target.message ? target.message.id : target.userId,
        room_id: target.roomId,
        category,
        ...(trimmed ? { details: trimmed } : {}),
      });
      setStep("done");
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const submitBlock = async () => {
    if (busy || isSelf) return;
    setBusy(true); setError("");
    try {
      const result = await apiPut<{ blocked: boolean }>(`/v1/safety/blocks/${encodeURIComponent(target.userId)}`, { room_id: target.roomId });
      if (result?.blocked !== true) throw new Error("ブロックできませんでした。");
      queryClient.setQueryData<BlockedUser[]>(blocksQueryKey(session?.tenantId, session?.user.id), old =>
        old?.some(item => item.user_id === target.userId) ? old : [...(old ?? []), { user_id: target.userId, display_name: target.displayName, created_at: Date.now() }]);
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
      setBusy(false);
      onClose();
    } catch (e) { fail(e); setBusy(false); }
  };

  const title = step === "report" ? "通報" : step === "confirm" ? "通報内容の確認" : step === "done" ? "通報を受け付けました" : "ブロック";
  const preview = target.message ? (
    <p className="p-3 rounded-md bg-surface-raised text-sm text-fg-primary whitespace-pre-wrap break-words max-h-40 overflow-auto">{target.message.body}</p>
  ) : null;

  return (
    <Modal open onClose={close} title={title} size="md">
      <div className="space-y-3 text-sm text-fg-primary">
        {isSelf ? <p>自分の投稿は通報・ブロックできません。</p> : null}
        {!isSelf && step === "report" && <>
          <p>対象: {target.displayName}</p>
          {preview ?? <p>このメンバーを通報します。</p>}
          <p className="font-medium">理由</p>
          <div className="flex flex-wrap gap-2" role="group" aria-label="理由">
            {REPORT_CATEGORIES.map(item => (
              <button
                key={item.id}
                type="button"
                aria-pressed={category === item.id}
                onClick={() => setCategory(item.id)}
                className={cn(
                  "px-3 py-1.5 rounded-full border text-sm focus-visible:shadow-focus",
                  category === item.id ? "bg-brand-600 border-brand-600 text-fg-inverse" : "border-border-default text-fg-primary hover:bg-surface-raised",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
          <textarea
            aria-label="通報の補足"
            placeholder="補足（任意）"
            value={details}
            maxLength={REPORT_DETAILS_MAX}
            onChange={e => setDetails(e.target.value)}
            className="w-full min-h-20 rounded-md border border-border-default bg-canvas p-2 text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={close}>キャンセル</Button>
            <Button disabled={!category} onClick={() => {
              if (trimmed.length > REPORT_DETAILS_MAX) { setError(`補足は${REPORT_DETAILS_MAX}文字以内にしてください。`); return; }
              setError(""); setStep("confirm");
            }}>内容を確認</Button>
          </div>
        </>}
        {!isSelf && step === "confirm" && category && <>
          <p>対象: {target.displayName}</p>
          {preview}
          <p>理由: {REPORT_CATEGORIES.find(item => item.id === category)?.label}</p>
          <p>{trimmed ? `補足: ${trimmed}` : "補足はありません。"}</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={busy} onClick={() => setStep("report")}>戻って修正</Button>
            <Button disabled={busy} onClick={() => void submitReport()}>{busy ? "送信しています…" : "通報を送信"}</Button>
          </div>
        </>}
        {!isSelf && step === "done" && <>
          <p>ワークスペースの管理者に通報を送りました。通報だけでは相手はブロックされません。</p>
          <div className="flex justify-end"><Button onClick={close}>閉じる</Button></div>
        </>}
        {!isSelf && step === "block" && <>
          <p>{target.displayName} をブロックします。このワークスペースでは相手の投稿を非表示にし、相手とのダイレクトメッセージと、そのDM内の通話を利用できなくなります。通報は別の操作です。</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={busy} onClick={close}>キャンセル</Button>
            <Button variant="danger" disabled={busy} onClick={() => void submitBlock()}>{busy ? "ブロックしています…" : "ブロックする"}</Button>
          </div>
        </>}
        {isSelf && <div className="flex justify-end"><Button variant="secondary" onClick={close}>閉じる</Button></div>}
        {error ? <p role="alert" className="text-danger">{error}</p> : null}
      </div>
    </Modal>
  );
}
